/**
 * Inhalt einer Mail im Mail-Fenster (docs/KONZEPT-GESPRAECH-MAILFENSTER.md Punkt 4).
 *
 *  - HTML-Mail: `react-letter` (sanitisiert im Browser per DOMParser; <script>, on*-Attribute und
 *    fremde Schemata fliegen raus, Klassen/IDs bekommen ein Praefix und <style>-Regeln werden darauf
 *    eingegrenzt — das App-CSS bleibt unberuehrt). KEIN iframe: die Hoehe waere nicht messbar.
 *  - Externe Bilder (Tracking-Pixel!) werden erst nach Klick „Bilder laden“ nachgeladen.
 *  - Ohne HTML: Klartext mit anklickbaren Links; Signatur und zitierte fruehere Nachricht klappen
 *    ein (`lib/gespraech.ts::gliedereGespraechNachricht`, Inhalt geht nie verloren).
 */
import { useCallback, useMemo, useState } from "react";
import { Letter } from "react-letter";
import { entferneMailClientArtefakte, kuerzeUrl } from "../../lib/mail-anzeige";
import { gliedereGespraechNachricht } from "../../lib/gespraech";
import type { GespraechEmailRow } from "./gespraech-typen";

const URL_MUSTER = /(https?:\/\/[^\s<>"]+)/g;

/** 1x1 transparentes GIF — Platzhalter fuer noch nicht geladene Bilder (kein Netzzugriff). */
const LEERES_BILD = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** Text mit anklickbaren, gekuerzten Links — React-Kinder, nie HTML. */
export function MitLinks({ text }: { text: string }) {
  return (
    <>
      {text.split(URL_MUSTER).map((stueck, i) =>
        i % 2 === 1 ? (
          <a key={i} href={stueck} target="_blank" rel="noopener noreferrer" title={stueck} className="text-primary underline-offset-2 hover:underline">
            {kuerzeUrl(stueck)}
          </a>
        ) : (
          stueck
        ),
      )}
    </>
  );
}

/** Ein einklappbarer Abschnitt (Signatur ODER Zitat) unter dem sichtbaren Nachrichtentext. */
function EinklappAbschnitt({ knopfText, text }: { knopfText: string; text: string }) {
  const [offen, setOffen] = useState(false);
  if (!offen) {
    return (
      <button type="button" onClick={() => setOffen(true)} className="mt-1 font-body-sm text-[13px] font-semibold text-primary hover:underline">
        {knopfText}
      </button>
    );
  }
  return (
    <div className="mt-2 border-t border-outline-variant/30 pt-2 dark:border-outline/20">
      <p className="whitespace-pre-wrap font-body-sm text-[13.5px] leading-[1.5] text-on-surface-variant opacity-80 [overflow-wrap:anywhere]">
        <MitLinks text={text} />
      </p>
      <button type="button" onClick={() => setOffen(false)} className="mt-1 font-body-sm text-[13px] font-semibold text-secondary hover:underline dark:text-secondary-fixed-dim">
        Einblenden schließen
      </button>
    </div>
  );
}

const ERLAUBTE_SCHEMATA = ["http", "https", "mailto"];
/** Gibt es im HTML ueberhaupt etwas Externes nachzuladen? (<img> oder CSS url(...)). */
const HAT_BILDER = /<img[\s>]|url\(/i;
const HAT_ZITAT = /<blockquote[\s>]/i;

function HtmlKoerper({ html, text }: { html: string; text: string }) {
  const [bilderGeladen, setBilderGeladen] = useState(false);
  const [zitatOffen, setZitatOffen] = useState(false);
  const hatBilder = useMemo(() => HAT_BILDER.test(html), [html]);
  const hatZitat = useMemo(() => HAT_ZITAT.test(html), [html]);
  // Stabile Identitaet: `Letter` ist memoisiert und saniert bei jeder Prop-Aenderung neu.
  const umschreiben = useCallback((url: string) => (bilderGeladen ? url : LEERES_BILD), [bilderGeladen]);

  return (
    <div>
      {hatBilder && !bilderGeladen && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-surface-container-high px-3 py-1.5 font-body-sm text-[13px] text-on-surface-variant dark:bg-white/[0.05]">
          <span>Externe Bilder sind ausgeblendet.</span>
          <button type="button" data-testid="gespraech-bilder-laden" onClick={() => setBilderGeladen(true)} className="font-semibold text-primary hover:underline">
            Bilder laden
          </button>
        </div>
      )}
      <div data-testid="gespraech-mail-html" data-zitat={hatZitat ? (zitatOffen ? "offen" : "zu") : undefined} className={`gespraech-mail-html font-body-sm text-[15px] leading-[1.55] text-on-surface dark:text-inverse-on-surface ${hatZitat && !zitatOffen ? "[&_blockquote]:hidden" : ""}`}>
        <Letter html={html} text={text} allowedSchemas={ERLAUBTE_SCHEMATA} rewriteExternalResources={umschreiben} />
      </div>
      {hatZitat && (
        <button type="button" data-testid="gespraech-zitat-umschalten" onClick={() => setZitatOffen((o) => !o)} className="mt-1 font-body-sm text-[13px] font-semibold text-primary hover:underline">
          {zitatOffen ? "Zitierte Nachricht ausblenden" : "Zitierte Nachricht anzeigen"}
        </button>
      )}
    </div>
  );
}

export function GespraechMailKoerper({ email }: { email: Pick<GespraechEmailRow, "bodyText" | "bodyHtml"> }) {
  const rohtext = entferneMailClientArtefakte(email.bodyText)?.trim() || "";
  const html = email.bodyHtml?.trim() || "";
  const gliederung = useMemo(() => gliedereGespraechNachricht(rohtext), [rohtext]);
  if (html) return <HtmlKoerper html={html} text={rohtext} />;
  return (
    <div>
      <div className="min-w-0 whitespace-pre-wrap font-body-sm text-[15px] leading-[1.55] text-on-surface [overflow-wrap:anywhere] dark:text-inverse-on-surface">
        <MitLinks text={gliederung.sichtbar || "(Kein Text)"} />
      </div>
      {gliederung.signatur && <EinklappAbschnitt knopfText={`Signatur und Rechtshinweis anzeigen (${gliederung.signatur.zeilen} Zeilen)`} text={gliederung.signatur.text} />}
      {gliederung.zitat && <EinklappAbschnitt knopfText="Zitierte frühere Nachricht anzeigen" text={gliederung.zitat.text} />}
    </div>
  );
}
